import { Component, Input, OnInit } from '@angular/core';
import { getDistanceFromLatLonInKm } from '@core/util/address';
import { Address } from '@models/address';
import { MatterFieldMappings, MatterFileType, MatterLRO } from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { MatterActions } from '@state/matter';
import { Observable } from 'rxjs';
import { LROData } from 'src/app/shared/data/LRO';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { LRO } from '@models/lro';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { cloneDeep } from 'lodash';
import { LTTSelector } from '@state/global/ltt.selectors';
import { SnackbarService } from '@services/ui/snackbar.service';
import { SearchableDropdownItems } from 'src/app/shared/atomic/atoms/searchable-dropdown/searchable-dropdown.types';
import { TestHooks } from './lro.hooks';
import { AI_GENERATED_ICON } from '@core/util/icon';

@UntilDestroy()
@Component({
	selector: 'app-sale-lro',
	templateUrl: './lro.component.html',
	styleUrls: ['./lro.component.scss'],
	standalone: false,
})
export class LroComponent implements OnInit {
	testHooks = TestHooks;
	@Select(SelectedMatter.address) selectedAddress$: Observable<Address>;
	@Select(SelectedMatter.LRO) saleLRO$: Observable<MatterLRO>;
	@Select(SelectedMatter.getFieldMappings)
	fieldMappings$: Observable<MatterFieldMappings>;
	@SelectSnapshot(LTTSelector.isMlttApplied) isMlttApplied: boolean;
	@SelectSnapshot(SelectedMatter.id) matterId;
	@SelectSnapshot(SelectedMatter.matterFileType) matterFileType: MatterFileType;

	@Input() required = false;
	@Input() showAiIcon = false;

	private _lros = cloneDeep(LROData);
	public sortedLROs: SearchableDropdownItems[] = [];

	public AI_ICON = AI_GENERATED_ICON.icon;
	public AI_TOOLTIP = AI_GENERATED_ICON.tooltip;
	public AI_CLASSES = AI_GENERATED_ICON.classes;

	public saleLRO: MatterLRO;
	constructor(private snackbarService: SnackbarService) {}

	ngOnInit(): void {
		this.saleLRO$.pipe(untilDestroyed(this)).subscribe((lro) => {
			this.saleLRO = lro;
		});

		this.selectedAddress$.pipe(untilDestroyed(this)).subscribe((value) => {
			this.sortedLROs = this.orderByDistance(
				value,
				Object.values(this._lros),
			).map((lro) => ({
				label: `${lro.name} (LRO ${lro.id})`,
				value: lro,
			}));
		});
	}

	orderByDistance(address: Address, lros: LRO[]): Partial<LRO>[] {
		if (address?._geoloc?.lat && address?._geoloc?.lng) {
			const distances = Object.values(this._lros).map((lro) => ({
				...lro,
				distance: getDistanceFromLatLonInKm(
					address._geoloc.lat,
					address._geoloc.lng,
					lro.address._geoloc.lat,
					lro.address._geoloc.lng,
				),
			}));

			distances.sort((a, b) => a.distance - b.distance);

			return distances.map((lro) => {
				delete lro.distance;
				return lro;
			});
		}

		return lros;
	}

	@Dispatch()
	_clearLRO(_matterId: string): MatterActions.DeleteSelectedLRO {
		return new MatterActions.DeleteSelectedLRO(_matterId);
	}

	@Dispatch()
	_selectLRO(_matterId: string, lro: LRO): MatterActions.SetLRO {
		return new MatterActions.SetLRO({ matterId: _matterId, lro });
	}

	selectLRO(event: LRO): void {
		this._selectLRO(this.matterId, event);
	}

	clearLRO(): void {
		this._clearLRO(this.matterId);
	}
}
